import { IonContent, IonPage, IonItem, IonInput, IonButton, IonImg, IonGrid, IonRow, IonCol, IonText, IonSpinner } from '@ionic/react';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import SmartDocsLogo from '../assets/SmartDocsTextLogoLight.png';
import { authApi } from '../services/authApi';
import { useAuth } from '../context/AuthContext';
import './SignIn.css';
import './AuthPages.css';

const ResetPassword: React.FC = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [tokenError, setTokenError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const { logout } = useAuth();

  const token = new URLSearchParams(location.search).get('token');

  useEffect(() => {
    if (!token) {
      setTokenError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      await authApi.resetPassword(token, newPassword, confirmPassword);
      // Clear session so SignIn does not redirect back to app. Defer navigation
      // so React flushes logout state before SignIn mounts (avoids redirect loop).
      logout();
      const message = 'Password reset successfully. Please sign in with your new password.';
      setTimeout(() => {
        history.replace('/signin', { message });
      }, 0);
    } catch (err) {
      const apiError = err as { message?: string };
      setError(apiError.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <IonPage className="sign-in-page">
      <IonContent className="sign-in-content" fullscreen={false}>
        <div className="sign-in-wrapper">
          <IonGrid className="ion-text-center sign-in-grid">
            <IonRow className="ion-justify-content-center">
              <IonCol size="12" sizeMd="6" sizeLg="4">
                <div className="logo-container">
                  <IonImg src={SmartDocsLogo} alt="SmartDocs Logo" className="logo" />
                </div>

                <h2 className="auth-page-title">Set New Password</h2>

                {tokenError ? (
                  <div className="auth-message-container">
                    <IonText color="danger" className="error-text">
                      {tokenError}
                    </IonText>
                    <IonButton
                      type="button"
                      fill="clear"
                      className="auth-link-button"
                      onClick={() => history.push('/forgot-password')}
                    >
                      Request New Reset Link
                    </IonButton>
                    <IonButton
                      type="button"
                      fill="clear"
                      className="auth-link-button"
                      onClick={() => history.push('/signin')}
                    >
                      Back to Sign In
                    </IonButton>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="sign-in-form">
                    <IonItem className="custom-input no-border">
                      <IonInput
                        data-testid="reset-password-new"
                        type="password"
                        value={newPassword}
                        onIonInput={e => setNewPassword(e.detail.value || '')}
                        required
                        className="input-field no-autofill-border"
                        autocomplete="new-password"
                        label="New Password"
                        labelPlacement="floating"
                        color="light"
                      />
                    </IonItem>

                    <IonItem className="custom-input no-border">
                      <IonInput
                        data-testid="reset-password-confirm"
                        type="password"
                        value={confirmPassword}
                        onIonInput={e => setConfirmPassword(e.detail.value || '')}
                        required
                        className="input-field no-autofill-border"
                        autocomplete="new-password"
                        label="Confirm Password"
                        labelPlacement="floating"
                        color="light"
                      />
                    </IonItem>

                    <IonText className="auth-helper-text">
                      Password must be at least 6 characters long.
                    </IonText>

                    {error && (
                      <IonText color="danger" className="error-text">
                        {error}
                      </IonText>
                    )}

                    <IonButton
                      expand="block"
                      type="submit"
                      className="sign-in-button"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <div className="loading-container">
                          <IonSpinner name="crescent" />
                          <span className="loading-text">Resetting...</span>
                        </div>
                      ) : (
                        'Reset Password'
                      )}
                    </IonButton>

                    <IonButton
                      type="button"
                      fill="clear"
                      className="auth-link-button"
                      onClick={() => history.push('/signin')}
                    >
                      Back to Sign In
                    </IonButton>
                  </form>
                )}
              </IonCol>
            </IonRow>
          </IonGrid>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default ResetPassword;
